import useApiHook from '../../../../hooks/api/useApiHook';
import { useCallback, useEffect } from 'react';
import { serializeObjectToUrl } from '../../../../utils/urlHelper';

const CONTRACT_API_ROOT = '/contracts';

export const useFetchContract = (contractId) => {
  const { getContract, data, error, loading } = useGetContract();
  useEffect(() => {
    const fetchData = async () => {
      await getContract(contractId);
    };
    fetchData();
  }, [getContract, contractId]);
  return {
    contract: data,
    error,
    loading,
  };
};

export const useFetchContractProductQuantities = (contractId) => {
  const { getContractProductQuantities, data, error, loading } = useGetContractProductQuantities();

  const refreshQuantities = useCallback(async () => {
    await getContractProductQuantities(contractId);
  }, [contractId, getContractProductQuantities]);
  useEffect(() => {
    refreshQuantities();
  }, [refreshQuantities]);

  return {
    quantities: data,
    error,
    loading,
    refreshQuantities,
  };
};

export const useGetContract = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContract = useCallback(
    async (contractId) => {
      await get(`${CONTRACT_API_ROOT}/${contractId}`);
    },
    [get],
  );
  return { getContract, data, error, loading };
};

export const useGetContractProductQuantities = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractProductQuantities = useCallback(
    async (contractId) => {
      await get(`${CONTRACT_API_ROOT}/${contractId}/product-quantities`);
    },
    [get],
  );

  return { getContractProductQuantities, data, error, loading };
};

export const useGetContractRemainingQuantityExport = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractRemainingQuantityExport = useCallback(
    async (contractId) => {
      await get(`${CONTRACT_API_ROOT}/${contractId}/export-not-purchased-quantities`, true, { responseType: 'blob' });
    },
    [get],
  );

  return { getContractRemainingQuantityExport, data, error, loading };
};

export const useGetContractTariffPricesExport = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractTariffPricesExport = useCallback(
    async (contractId) => {
      await get(`${CONTRACT_API_ROOT}/${contractId}/tariff-prices/export`, true, { responseType: 'blob' });
    },
    [get],
  );

  return { getContractTariffPricesExport, data, error, loading };
};

export const useGetContractExport = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractExport = useCallback(
    async (contractId) => {
      await get(`${CONTRACT_API_ROOT}/${contractId}/contractExport`, true, { responseType: 'blob' });
    },
    [get],
  );

  return { getContractExport, data, error, loading };
};

export const useGetContractProductDates = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractProductDates = useCallback(
    async (contractId) => {
      await get(`/contracts/${contractId}/product-dates`);
    },
    [get],
  );

  return { getContractProductDates, data, error, loading };
};

export const useGetTariffPrices = () => {
  const { get, data, error, loading } = useApiHook(true);
  const getTariffPrices = useCallback(
    async (contractId, query) => {
      await get(`/contracts/${contractId}/tariff-prices?${serializeObjectToUrl(query)}`);
    },
    [get],
  );

  return { getTariffPrices, data, error, loading };
};

export const useGetContractPurchasePrices = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getContractPurchasePrices = useCallback(
    async (contractId) => {
      await get(`/contracts/${contractId}/purchase-prices`);
    },
    [get],
  );

  return { getContractPurchasePrices, data, error, loading };
};

export const usePostContract = () => {
  const { post, data, error, loading } = useApiHook(true);

  const postContract = useCallback(
    async (contractId, data) => {
      await post(`/contracts/${contractId}/comment`, data);
    },
    [post],
  );

  return { postContract, data, error, loading };
};

export const useGetProductsForMultContracts = () => {
  const { get, data, error, loading } = useApiHook(true);

  const getProducts = useCallback(
    async (commodityId, contractPeriod) => {
      await get(`/exchange-products/productsWithBestAskPrices?commodity=${commodityId}&year=${contractPeriod}`);
    },
    [get],
  );

  return { getProducts, data, error, loading };
};

export const usePostProductsForMultContracts = () => {
  const { post, data, error, loading } = useApiHook(true);

  const postProducts = useCallback(
    async (data) => {
      await post(`/purchases/bulkBuyBestAsk`, data);
    },
    [post],
  );

  return { postProducts, data, error, loading };
};
